// this will use Dexie.js to create a database, all the tables and indexes. All methods are async, so you can use await to wait for the result.
// All types are defined in the interfaces.ts file.

import Dexie, { type IndexableType } from "dexie";
import type { Loggers } from "../../workers/Logger/helpers";

import type { DbInfo, IAddress } from "./interface";

export class MainDatabase extends Dexie {
	// @ts-ignore
	dbInfo!: Dexie.Table<DbInfo, string>;
	BookingPaymentAddresses!: Dexie.Table<IAddress, number>;
	LandlordAddresses!: Dexie.Table<IAddress, number>;
	PropertyAddresses!: Dexie.Table<IAddress, number>;
	ServiceAgreementBillingAddresses!: Dexie.Table<IAddress, number>;
	ServiceAgreementCustomerAddresses!: Dexie.Table<IAddress, number>;
	ServiceAgreementPolicyAddresses!: Dexie.Table<IAddress, number>;
	logger!: Loggers["logger"];

	constructor() {
		super("MainDatabase");
		// Version 2 - updated schema, renamed table BookingPaymentBillingAddresses to BookingPaymentAddresses
		this.version(2)
			.stores({
				dbInfo: "version, created",
				BookingPaymentAddresses:
					"id, organisation_Name, building_Name, sub_Building_Name, building_Number, addressString",
				LandlordAddresses:
					"id, organisation_Name, building_Name, sub_Building_Name, building_Number, addressString",
				PropertyAddresses:
					"id, organisation_Name, building_Name, sub_Building_Name, building_Number, addressString",
				ServiceAgreementBillingAddresses:
					"id, organisation_Name, building_Name, sub_Building_Name, building_Number, addressString",
				ServiceAgreementCustomerAddresses:
					"id, organisation_Name, building_Name, sub_Building_Name, building_Number, addressString",
				ServiceAgreementPolicyAddresses:
					"id, organisation_Name, building_Name, sub_Building_Name, building_Number, addressString",
			})
			.upgrade(async () => {
				// Check if the old table exists and manually delete it
				if (
					this.tables.some(
						(table) => table.name === "BookingPaymentBillingAddresses",
					)
				) {
					await this.table("BookingPaymentBillingAddresses").clear();
					delete this._allTables["BookingPaymentBillingAddresses"];
				}
			});

		this.dbInfo = this.table("dbInfo");
		this.BookingPaymentAddresses = this.table("BookingPaymentAddresses");
		this.LandlordAddresses = this.table("LandlordAddresses");
		this.PropertyAddresses = this.table("PropertyAddresses");
		this.ServiceAgreementBillingAddresses = this.table(
			"ServiceAgreementBillingAddresses",
		);
		this.ServiceAgreementCustomerAddresses = this.table(
			"ServiceAgreementCustomerAddresses",
		);
		this.ServiceAgreementPolicyAddresses = this.table(
			"ServiceAgreementPolicyAddresses",
		);
		this.logger = console;
	}

	async getDbInfo(): Promise<DbInfo | undefined> {
		const dbInfo = await this.dbInfo.get("dbInfo");
		if (!dbInfo) {
			this.logger.error("MainDatabase", "getDbInfo - No dbInfo found");
			return;
		}
		return dbInfo;
	}

	async setDbInfo(dbInfo: DbInfo): Promise<void> {
		await this.dbInfo.put(dbInfo);
	}

	async getBookingPaymentAddress(id: number): Promise<IAddress | undefined> {
		const address = await this.BookingPaymentAddresses.get(id);
		if (!address) {
			this.logger.error(
				"MainDatabase",
				"getBookingPaymentAddress - No address found",
			);
			return;
		}
		return address;
	}

	async setBookingPaymentAddress(address: IAddress): Promise<void> {
		await this.BookingPaymentAddresses.put(address);
	}

	async getLandlordAddress(id: number): Promise<IAddress | undefined> {
		const address = await this.LandlordAddresses.get(id);
		if (!address) {
			this.logger.error(
				"MainDatabase",
				"getLandlordAddress - No address found",
			);
			return;
		}
		return address;
	}

	async setLandlordAddress(address: IAddress): Promise<void> {
		await this.LandlordAddresses.put(address);
	}

	async getPropertyAddress(id: number): Promise<IAddress | undefined> {
		const address = await this.PropertyAddresses.get(id);
		if (!address) {
			this.logger.error(
				"MainDatabase",
				"getPropertyAddress - No address found",
			);
			return;
		}
		return address;
	}

	async setPropertyAddress(address: IAddress): Promise<void> {
		await this.PropertyAddresses.put(address);
	}

	async getServiceAgreementBillingAddress(
		id: number,
	): Promise<IAddress | undefined> {
		const address = await this.ServiceAgreementBillingAddresses.get(id);
		if (!address) {
			this.logger.error(
				"MainDatabase",
				"getServiceAgreementBillingAddress - No address found",
			);
			return;
		}
		return address;
	}

	async setServiceAgreementBillingAddress(address: IAddress): Promise<void> {
		await this.ServiceAgreementBillingAddresses.put(address);
	}

	async getServiceAgreementCustomerAddress(
		id: number,
	): Promise<IAddress | undefined> {
		const address = await this.ServiceAgreementCustomerAddresses.get(id);
		if (!address) {
			this.logger.error(
				"MainDatabase",
				"getServiceAgreementCustomerAddress - No address found",
			);
			return;
		}
		return address;
	}

	async setServiceAgreementCustomerAddress(address: IAddress): Promise<void> {
		await this.ServiceAgreementCustomerAddresses.put(address);
	}

	async getServiceAgreementPolicyAddress(
		id: number,
	): Promise<IAddress | undefined> {
		const address = await this.ServiceAgreementPolicyAddresses.get(id);
		if (!address) {
			this.logger.error(
				"MainDatabase",
				"getServiceAgreementPolicyAddress - No address found",
			);
			return;
		}
		return address;
	}

	async setServiceAgreementPolicyAddress(address: IAddress): Promise<void> {
		await this.ServiceAgreementPolicyAddresses.put(address);
	}

	async clearAll(): Promise<void> {
		await this.BookingPaymentAddresses.clear();
		await this.LandlordAddresses.clear();
		await this.PropertyAddresses.clear();
		await this.ServiceAgreementBillingAddresses.clear();
		await this.ServiceAgreementCustomerAddresses.clear();
		await this.ServiceAgreementPolicyAddresses.clear();
	}

	getTableByName(name: IndexableType): Dexie.Table<any, any> {
		return this.tables.find((table) => table.name === name) as Dexie.Table<
			any,
			any
		>;
	}
}
